import React from "react";
import { L, tr } from "@/lib/i18n";
import { TAP_MAX_PX } from "@/lib/gestureThresholds";
import { visibleUnitFields, formatFieldValue, fieldLabel } from "@/lib/unitFinder";
import { statusPill } from "./statusUtil";

/**
 * polyCenter — geometric centroid of a normalized polygon. Used internally
 * to anchor the ZoneHoverCard above the zone.
 */
function polyCenter(pts) {
  if (!pts?.length) return {};
  const cx = pts.reduce((a, p) => a + p.x, 0) / pts.length;
  const cy = pts.reduce((a, p) => a + p.y, 0) / pts.length;
  return { x: cx, y: cy };
}

/**
 * ZoneLayer — all interactive zones rendered on top of the active scene.
 *
 * Two shapes:
 *  - polygon → soft-stroked SVG <polygon>
 *  - point   → pulsing accent-colour dot (the "twinkle" added in V1.1)
 *
 * Behaviour:
 *  - On hover (desktop) or first tap (touch) we surface a ZoneHoverCard.
 *  - On a confirmed activation (second tap or click of the hover-card
 *    "Open" action) the parent's onZoneClick decides what happens —
 *    open unit/group details, show info, or jump to another scene.
 */
export default function ZoneLayer({
  zones,
  inventory,
  scenes,
  project,
  mapPoint,
  containerSize,
  previewZoneId,
  setPreviewZoneId,
  onZoneClick,
  lang,
  defaultLang,
  fieldDefinitions = [],
  highlightZoneIds = null, // Phase 4: Set of zone IDs to spotlight
}) {
  const polyStr = (pts) => pts.map(mapPoint).map(p => `${p.x},${p.y}`).join(" ");
  const hasHighlight = highlightZoneIds && highlightZoneIds.size > 0;
  const isHighlit = (id) => hasHighlight && highlightZoneIds.has(id);

  // [iter55] Deterministic two-step interaction (fixes desktop "click does
  // nothing"). Previously BOTH pointerup and the synthetic click fired and
  // double-handled the zone (activate → re-open preview), so on a mouse the
  // net effect was unreliable. Now:
  //   • 1st click/tap on a zone → surface & HOLD its info card (arm it)
  //   • 2nd click/tap on the SAME zone → perform the action
  //   • the card's "{L(lang, "details")}" button still activates in one click
  // pointerup is the single source of truth; the click event is deduped.
  const pressRef = React.useRef({}); // zone.id -> { x, y, wasDrag }
  const armedRef = React.useRef(null); // zone.id primed by a first click/tap
  const handledAtRef = React.useRef(0); // timestamp to dedupe pointerup vs click

  React.useEffect(() => { if (!previewZoneId) armedRef.current = null; }, [previewZoneId]);

  const activate = (zone) => {
    if (armedRef.current === zone.id) {
      onZoneClick(zone);
      setPreviewZoneId(null);
      armedRef.current = null;
    } else {
      armedRef.current = zone.id;
      setPreviewZoneId(zone.id);
    }
  };

  const beginPress = (zone, e) => {
    pressRef.current[zone.id] = { x: e.clientX, y: e.clientY, wasDrag: false };
    // No stopPropagation — SceneCanvas needs to see this pointerdown for drag.
  };
  const endPress = (zone, e) => {
    const p = pressRef.current[zone.id];
    if (!p) return;
    const wasDrag = Math.max(Math.abs(e.clientX - p.x), Math.abs(e.clientY - p.y)) > TAP_MAX_PX;
    pressRef.current[zone.id] = { ...p, wasDrag };
    if (!wasDrag) {
      handledAtRef.current = Date.now();
      activate(zone);
    }
    // If it was a drag, do NOTHING — SceneCanvas already engaged scrub/rotate.
  };
  const onZoneClickInner = (zone, e) => {
    if (e.detail === 0) { e.stopPropagation(); activate(zone); return; }
    // pointerup already handled this exact interaction — swallow the click.
    if (Date.now() - handledAtRef.current < 500) { e.stopPropagation(); return; }
    const p = pressRef.current[zone.id];
    if (p?.wasDrag) { e.preventDefault(); e.stopPropagation(); return; }
    // Keyboard / synthetic click fallback (no preceding pointer sequence).
    e.stopPropagation();
    activate(zone);
  };

  return (
    <>
      {/* Phase 4 — dim layer behind highlight zones for cinematic spotlight */}
      {hasHighlight && (
        <div className="absolute inset-0 pointer-events-none bg-black/55 transition-opacity duration-500" data-testid="presentation-spotlight-dim" />
      )}
      <svg
        className="absolute inset-0"
        width={containerSize.w}
        height={containerSize.h}
        viewBox={`0 0 ${containerSize.w} ${containerSize.h}`}
        preserveAspectRatio="none"
        data-testid="viewer-zone-layer"
      >
        {zones.filter(z => (z.shape || "polygon") === "polygon" && z.polygon_points?.length).map((z) => {
          const unit = z.entity_id ? inventory.entities.find(e => e.id === z.entity_id) : null;
          const cls = "zone-poly " + (unit?.status === "sold" ? "zone-sold" : unit?.status === "reserved" ? "zone-reserved" : "zone-available");
          const hl = isHighlit(z.id);
          return (
            <polygon
              key={z.id}
              points={polyStr(z.polygon_points)}
              className={cls + (previewZoneId === z.id ? " active" : "") + (hl ? " zone-highlight" : "") + (hasHighlight && !hl ? " zone-muted" : "")}
              data-zone-interactive="true"
              role="button" tabIndex={0}
              aria-label={tr(z, "name", lang, defaultLang) || L(lang, "details")}
              onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(z); } }}
              onPointerDown={(e) => beginPress(z, e)}
              onPointerUp={(e) => endPress(z, e)}
              onClick={(e) => onZoneClickInner(z, e)}
              onMouseEnter={() => setPreviewZoneId(z.id)}
              onMouseLeave={() => { if (armedRef.current !== z.id) setPreviewZoneId(cur => cur === z.id ? null : cur); }}
              data-testid={`viewer-zone-${z.id}`}
              data-highlight={hl ? "true" : undefined}
            />
          );
        })}
      </svg>

      {/* Twinkle / point zones — rendered above the SVG so they stay crisp
          at any zoom without affecting polygon coords. */}
      {zones.filter(z => z.shape === "point" && z.point_coord).map((z) => {
        const screen = mapPoint(z.point_coord);
        const active = previewZoneId === z.id;
        const hl = isHighlit(z.id);
        return (
          <button
            key={z.id}
            type="button"
            data-zone-interactive="true"
            data-testid={`viewer-zone-point-${z.id}`}
            onPointerDown={(e) => beginPress(z, e)}
            onPointerUp={(e) => endPress(z, e)}
            onClick={(e) => onZoneClickInner(z, e)}
            onMouseEnter={() => setPreviewZoneId(z.id)}
            onMouseLeave={() => { if (armedRef.current !== z.id) setPreviewZoneId(cur => cur === z.id ? null : cur); }}
            className={`zone-point ${active ? "is-active" : ""} ${hl ? "is-highlit" : ""} ${hasHighlight && !hl ? "is-muted" : ""}`}
            style={{ left: screen.x, top: screen.y, "--zone-accent": project.accent_color || "#C4A47C" }}
            aria-label={tr(z, "name", lang, defaultLang) || L(lang, "details")}
            data-highlight={hl ? "true" : undefined}
          >
            <span className="zone-point-dot" />
            <span className="zone-point-ping" />
          </button>
        );
      })}

      {/* Hover / first-tap preview cards anchored above the zone. */}
      {zones.map((z) => {
        if (previewZoneId !== z.id) return null;
        let screen;
        if (z.shape === "point" && z.point_coord) {
          screen = mapPoint(z.point_coord);
        } else {
          const center = polyCenter(z.polygon_points);
          if (center.x === undefined) return null;
          screen = mapPoint(center);
        }
        return (
          <ZoneHoverCard
            fieldDefinitions={fieldDefinitions}
            project={project}
            key={z.id}
            x={screen.x} y={screen.y}
            containerSize={containerSize}
            zone={z}
            inventory={inventory}
            scenes={scenes}
            lang={lang}
            defaultLang={defaultLang}
            onOpen={() => { onZoneClick(z); setPreviewZoneId(null); }}
          />
        );
      })}
    </>
  );
}

function ZoneHoverCard({ x, y, containerSize, zone, inventory, scenes, lang, defaultLang, onOpen, fieldDefinitions, project }) {
  const unit = zone.entity_id ? inventory.entities.find(e => e.id === zone.entity_id) : null;
  const group = zone.group_id ? inventory.groups.find(g => g.id === zone.group_id) : null;
  const action = zone.click_action || "open_details";
  const targetScene = (action === "goto_scene" && zone.target_scene_id && scenes)
    ? scenes.find(s => s.id === zone.target_scene_id) : null;

  const CARD_W = 240;
  const CARD_H = 130;
  const margin = 12;
  let left = x - CARD_W / 2;
  let top = y - CARD_H - 16;
  if (top < margin) top = y + 16;
  if (left < margin) left = margin;
  if (left + CARD_W > containerSize.w - margin) left = containerSize.w - CARD_W - margin;

  let body;
  if (action === "show_info") {
    const title = tr(zone, "info_title", lang, defaultLang) || tr(zone, "name", lang, defaultLang) || zone.name;
    const desc = tr(zone, "info_description", lang, defaultLang) || "";
    body = (
      <>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55">{L(lang, "info") || "Info"}</div>
        <div className="font-display text-lg text-white tracking-tight" data-testid={`hover-info-title-${zone.id}`}>{title}</div>
        {desc && <div className="text-[11px] text-white/70 mt-1 line-clamp-3">{desc}</div>}
        <button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#C4A47C] hover:text-white transition" data-testid={`hover-open-${zone.id}`}>
          {L(lang, "read_more")}
        </button>
      </>
    );
  } else if (action === "goto_scene" && targetScene) {
    body = (
      <>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55">{L(lang, "navigate")}</div>
        <div className="font-display text-lg text-white tracking-tight" data-testid={`hover-scene-name-${zone.id}`}>{tr(targetScene, "name", lang, defaultLang) || targetScene.name}</div>
        {zone.name && zone.name !== targetScene.name && <div className="text-[11px] text-white/70 mt-1">{tr(zone, "name", lang, defaultLang) || zone.name}</div>}
        <button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#C4A47C] hover:text-white transition" data-testid={`hover-open-${zone.id}`}>
          {L(lang, "go_to_view")}
        </button>
      </>
    );
  } else if (unit) {
    const name = tr(unit, "name", lang, defaultLang) || unit.name || unit.code;
    const rows = visibleUnitFields(unit, fieldDefinitions, "card").filter(({ key }) => ["price", "net_area", "gross_area", "area_sqm", "area"].includes(key) && (key !== "price" || project?.viewer_show_price !== false)).slice(0, 3);
    const type = L(lang, unit.type) || tr(unit, "type", lang, defaultLang) || unit.type;
    body = (
      <>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/55">{group ? (tr(group, "name", lang, defaultLang) || group.name) : (type || "Unit")}</div>
            <div className="font-display text-lg text-white tracking-tight" data-testid={`hover-unit-code-${zone.id}`}>{unit.code}</div>
            {name && name !== unit.code && <div className="text-[11px] text-white/65 mt-0.5">{name}</div>}
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${statusPill(unit.status)}`} data-testid={`hover-unit-status-${zone.id}`}>
            {L(lang, unit.status)}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-white/75">
          {rows.map(({ key, fd }) => <div key={key}><span className="text-white/45">{fieldLabel(fd, lang, defaultLang)}:</span> <span className="text-white">{formatFieldValue(unit, fd, lang, defaultLang, project)}</span></div>)}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#C4A47C] hover:text-white transition" data-testid={`hover-open-${zone.id}`}>
          {L(lang, "details")}
        </button>
      </>
    );
  } else if (group) {
    const blocks = new Set(inventory.groups.filter(g => g.parent_group_id === group.id).map(g => g.id));
    const ents = inventory.entities.filter(e => e.group_id === group.id || (group.group_type === "etap" && blocks.has(e.group_id)));
    const avail = ents.filter(e => e.status === "available").length;
    const gname = tr(group, "name", lang, defaultLang) || group.name;
    body = (
      <>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55">{L(lang, group.group_type === "etap" ? "phase" : "block")}</div>
        <div className="font-display text-lg text-white tracking-tight" data-testid={`hover-group-name-${zone.id}`}>{gname}</div>
        <div className="mt-2 text-[11px] text-white/75">
          <span className="text-white">{ents.length}</span> <span className="text-white/45">{L(lang, "total")}</span>
          {" · "}
          <span className="text-emerald-300">{avail}</span> <span className="text-white/45">{L(lang, "available").toLowerCase()}</span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#C4A47C] hover:text-white transition" data-testid={`hover-open-${zone.id}`}>
          {L(lang, "units")} →
        </button>
      </>
    );
  } else {
    body = (<div className="text-white text-sm">{tr(zone, "name", lang, defaultLang) || zone.name}</div>);
  }

  return (
    <div
      className="absolute z-20 pointer-events-auto glass-panel rounded-xl px-3.5 py-3 fade-in"
      style={{ left, top, width: CARD_W }}
      data-testid={`zone-hover-card-${zone.id}`}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      {body}
    </div>
  );
}
